import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '../../components/base-components';
import {
  usePlans,
  useEntitlement,
  useStartCheckout,
  useOpenBillingPortal,
  useChangePlan,
  useCancelTrial,
  useResumeTrial,
  queryKeys,
} from '../../hooks/useApi';
import { PaymentMethodRequiredError } from '../../api/client';
import { useToast } from '../../contexts/ToastContext';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { StatusPill, TrialBadge } from '../../components/SubscriptionStatus';
import {
  METER_LABELS,
  UsageMeter,
  formatUsd,
  MeterRate,
  MeterRateCopy,
  meterUnit,
  sortMeterKeys,
  visibleMeterKeys,
} from '../../components/UsageMeter';
import { FlashtoolsPlan, FlashtoolsMeterKey, FlashtoolsTier } from '../../types';
import {
  BillingSummaryStrip,
  InvoiceHistorySection,
  UpcomingInvoiceSection,
} from '../components/BillingDetails';
import { UsageDetailSection } from './ProfileScreen';
import { PageShell } from '../components/PageShell';
import { PageHeader } from '../components/PageHeader';
import { Panel } from '../components/Panel';
import { Badge } from '../components/Badge';
import { SegmentedControl } from '../components/SegmentedControl';
import { Skeleton } from '../components/Skeleton';
import { cx, FOCUS_RING } from '../components/styles';
import { v2Path } from '../nav';

/* ------------------------------------------------------------------ *
 * Logic block copied verbatim from src/pages/BillingPage.tsx.
 * Only the JSX and the className strings below the `return` are new.
 *
 * UX rework. Every hook, mutation, CTA branch, price string and confirm message
 * is v1's. What changed is that the page now shows the two facts it already
 * computed and then threw away:
 *
 *  - **Annual savings.** `planPricing` has computed `savePct` since the annual
 *    toggle shipped and no surface has ever rendered it, so the only claim
 *    about annual pricing was the hardcoded `2 months free` on the toggle pill.
 *    The real per-plan figure now sits on the price subnote. It is derived from
 *    the same two amounts already printed on the card, so it cannot drift from
 *    them, and the pill's copy is untouched.
 *  - **What you actually used.** The plan cards state allowances (`100 waitlist
 *    joins included`) with nothing to compare them against, while
 *    `entitlement.usage` — already loaded, already rendered as meters in the
 *    current-plan panel — holds the number that makes the allowance mean
 *    something. It is stated once under the plans heading rather than repeated
 *    on all three cards, and only for meters with non-zero usage.
 *
 * The current-plan card also moved its title and its `Manage billing` button
 * into the `Panel` header, so it reads like every other panel in the app
 * instead of hand-rolling a header inside its own body.
 * ------------------------------------------------------------------ */

const FEATURE_COPY: Record<string, { label: string; description: string }> = {
  seller_waitlists: {
    label: 'Seller Waitlists',
    description: 'See sold-out shows and waitlists for hot cities.',
  },
  buying_tools: {
    label: 'Buying Tools',
    description: 'Access Flash Buy to auto-purchase tickets.',
  },
};

// Blurbs describe what a tier is FOR. What it includes comes from the plan's
// own `features` / `limits` further down the card, so these must not restate an
// allowance or a rate — a number written here has no way to follow the API.
const TIER_BLURB: Record<string, string> = {
  core: 'Alerts, ticket inventory, transfers and listings across every account you own.',
  pro: 'Core plus Seller Waitlists — the tier that can join waitlists at volume.',
  reserve: 'Pro plus Buying Tools. Invite only.',
};

const TIER_RANK: Record<FlashtoolsTier, number> = {
  core: 1,
  pro: 2,
  reserve: 3,
};

// Trial terms come from the plans API — only some tiers offer one, and the
// length lives in billing.TRIAL_PERIOD_DAYS server-side. 0 = no trial.
function trialDays(plan: FlashtoolsPlan) {
  return plan.trial_period_days ?? 0;
}

function formatPrice(amount: string | number) {
  const n = typeof amount === 'number' ? amount : Number.parseFloat(amount);
  if (Number.isNaN(n)) return String(amount);
  return n % 1 === 0 ? `$${n.toFixed(0)}` : `$${n.toFixed(2)}`;
}

// Resolve what to show for a plan at the selected interval, including the
// effective monthly rate and savings when billed annually.
function planPricing(plan: FlashtoolsPlan, interval: 'month' | 'year') {
  if (interval === 'year' && plan.amount_annual) {
    const annual = Number.parseFloat(plan.amount_annual);
    const monthly = Number.parseFloat(plan.amount);
    const perMonth = annual / 12;
    const savePct = monthly > 0 ? Math.round((1 - perMonth / monthly) * 100) : 0;
    return {
      display: formatPrice(plan.amount_annual),
      unit: 'year',
      subnote: `${formatPrice(perMonth)}/mo, billed annually`,
      savePct,
    };
  }
  return { display: formatPrice(plan.amount), unit: 'month', subnote: null as string | null, savePct: 0 };
}

// Stripe needs absolute URLs; the origin + v2Path keeps the round trip inside
// the v2 shell so the success/cancel banners and entitlement poll can fire.
function successUrl() {
  return `${window.location.origin}${v2Path('/billing')}?status=success`;
}

function cancelUrl() {
  return `${window.location.origin}${v2Path('/billing')}?status=cancel`;
}

function returnUrl() {
  return `${window.location.origin}${v2Path('/billing')}`;
}

const CTA_BASE =
  'w-full rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50';

export const BillingScreen: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { showError, showSuccess } = useToast();
  const queryClient = useQueryClient();
  const plansQuery = usePlans();
  const entitlementQuery = useEntitlement();
  const startCheckout = useStartCheckout();
  const openPortal = useOpenBillingPortal();
  const changePlan = useChangePlan();
  const cancelTrial = useCancelTrial();
  const resumeTrial = useResumeTrial();
  const [confirmCancelTrial, setConfirmCancelTrial] = useState(false);
  const [pendingTier, setPendingTier] = useState<FlashtoolsTier | null>(null);
  const [confirmPlan, setConfirmPlan] = useState<FlashtoolsPlan | null>(null);
  const [interval, setInterval] = useState<'month' | 'year'>('month');

  const entitlement = entitlementQuery.data;
  const rawPlans = plansQuery.data?.plans;

  const statusParam = searchParams.get('status');

  // Webhook may trail the Checkout redirect by a few seconds. Poll the
  // entitlement briefly after returning from a successful checkout so the UI
  // reflects the new tier without the user refreshing.
  useEffect(() => {
    if (statusParam !== 'success') return;
    let attempts = 0;
    const id = window.setInterval(() => {
      queryClient.invalidateQueries({ queryKey: queryKeys.entitlement });
      attempts += 1;
      if (attempts >= 6) window.clearInterval(id);
    }, 2000);
    return () => window.clearInterval(id);
  }, [statusParam, queryClient]);

  const sortedPlans = useMemo(() => {
    if (!rawPlans) return [];
    const invited = Boolean(entitlement?.reserve_invited);
    const comped = Boolean(entitlement?.comped);
    return [...rawPlans]
      .filter(plan => {
        if (!plan.invite_only) return true;
        // Keep invite-only plans visible to invited users, the user already on
        // that tier, and admin-comped users; hide from everyone else.
        return invited || entitlement?.tier === plan.tier || comped;
      })
      .sort((a, b) => a.sort_order - b.sort_order);
  }, [rawPlans, entitlement?.reserve_invited, entitlement?.comped, entitlement?.tier]);

  // Annual is offered only when every visible plan has an annual price wired up,
  // so the toggle never lands the user in a mixed monthly/annual state.
  const annualAvailable = sortedPlans.length > 0 && sortedPlans.every(p => p.has_annual);
  const effectiveInterval: 'month' | 'year' = annualAvailable ? interval : 'month';

  // The meters the account has actually moved this period, in the same order the
  // meters render in. Purely a read of what's already loaded — an account with a
  // clean period, or no entitlement at all, produces an empty list and no line.
  const usedThisPeriod = useMemo(() => {
    const usage = entitlement?.usage ?? {};
    return sortMeterKeys(
      (Object.keys(usage) as FlashtoolsMeterKey[]).filter(k => (usage[k] ?? 0) > 0),
    ).map(key => ({ key, used: usage[key] as number }));
  }, [entitlement?.usage]);

  const handleSubscribe = async (plan: FlashtoolsPlan) => {
    if (plan.invite_only && !entitlement?.reserve_invited) {
      showError('Reserve is invite-only. Contact support to request access.');
      return;
    }
    try {
      const res = await startCheckout.mutateAsync({
        tier: plan.tier,
        successUrl: successUrl(),
        cancelUrl: cancelUrl(),
        interval: effectiveInterval,
      });
      window.location.href = res.url;
    } catch (err: any) {
      showError(err.message || 'Could not start checkout');
    }
  };

  const handleManage = async () => {
    try {
      const res = await openPortal.mutateAsync(returnUrl());
      window.location.href = res.url;
    } catch (err: any) {
      showError(err.message || 'Could not open billing portal');
    }
  };

  const requestChange = (plan: FlashtoolsPlan) => {
    if (plan.invite_only && !entitlement?.reserve_invited) {
      showError('Reserve is invite-only. Contact support to request access.');
      return;
    }
    setConfirmPlan(plan);
  };

  const confirmChange = async () => {
    const plan = confirmPlan;
    if (!plan) return;
    setPendingTier(plan.tier);
    try {
      await changePlan.mutateAsync({
        tier: plan.tier,
        interval: effectiveInterval,
        returnUrl: returnUrl(),
      });
      showSuccess(`Switched to ${plan.name}. Your usage and upcoming invoice reflect the new plan.`);
      setConfirmPlan(null);
    } catch (err: any) {
      // Switching mid-trial bills immediately and so needs a card on file.
      // Take the user to Stripe to add one — it becomes their default payment
      // method and they land back here — instead of telling them to go find
      // the billing portal themselves.
      if (err instanceof PaymentMethodRequiredError && err.url) {
        showError(err.message);
        window.location.href = err.url;
        return;
      }
      showError(err.message || 'Could not change plan');
    } finally {
      setPendingTier(null);
    }
  };

  const handleCancelTrial = async () => {
    try {
      const res = await cancelTrial.mutateAsync(undefined);
      const end = formatDate(res.entitlement.trial_end);
      showSuccess(end
        ? `Free trial cancelled. You keep access until ${end} and won't be charged.`
        : "Free trial cancelled. You won't be charged.");
      setConfirmCancelTrial(false);
    } catch (err: any) {
      showError(err.message || 'Could not cancel your trial');
    }
  };

  const handleResumeTrial = async () => {
    try {
      await resumeTrial.mutateAsync();
      showSuccess('Your trial is back on. Your plan continues after the trial.');
    } catch (err: any) {
      showError(err.message || 'Could not keep your trial');
    }
  };

  const dismissStatus = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('status');
    setSearchParams(next, { replace: true });
  };

  const breadcrumb = [{ label: 'Profile', to: v2Path('/profile') }, { label: 'Billing' }];
  const trialPlan = rawPlans?.find(p => p.tier === entitlement?.tier);

  if (plansQuery.isLoading || entitlementQuery.isLoading) {
    return (
      <PageShell>
        <PageHeader
          title="Billing"
          subtitle="Choose the plan that fits your workflow. Upgrade or downgrade anytime."
          breadcrumb={breadcrumb}
        />
        <div className="grid gap-5 md:grid-cols-3 items-stretch">
          {[0, 1, 2].map(i => (
            <div
              key={i}
              className="flex h-full flex-col gap-4 rounded-2xl border border-white/10 bg-white/5 p-6"
            >
              <Skeleton className="h-5 w-24" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-8 w-28" />
              <Skeleton className="h-3 w-4/5" />
              <Skeleton className="h-3 w-3/5" />
              <Skeleton className="mt-auto h-9 w-full" />
            </div>
          ))}
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageHeader
        title="Billing"
        subtitle="Choose the plan that fits your workflow. Upgrade or downgrade anytime."
        breadcrumb={breadcrumb}
      />

      <div className="flex flex-col gap-8">
        {/* The money question, answered before the plan chooser: someone opening
            Billing wants to know what they are paying now at least as often as
            they want to switch tier. Same components as Profile — see
            BillingDetails — so the two pages cannot disagree about a number. */}
        <BillingSummaryStrip />

        {statusParam === 'success' && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3">
            <span className="text-sm text-emerald-300">
              Subscription updated. It may take a few seconds to reflect here.
            </span>
            <button
              onClick={dismissStatus}
              className={cx('rounded text-sm text-emerald-300/80 hover:text-emerald-200', FOCUS_RING)}
            >
              Dismiss
            </button>
          </div>
        )}
        {statusParam === 'cancel' && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/5 px-4 py-3">
            <span className="text-sm text-white/70">Checkout cancelled. No changes were made.</span>
            <button
              onClick={dismissStatus}
              className={cx('rounded text-sm text-white/50 hover:text-white/80', FOCUS_RING)}
            >
              Dismiss
            </button>
          </div>
        )}

        {entitlement?.comped && !entitlement.tier && (
          <Panel>
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone="green">Admin access</Badge>
                <span className="text-lg font-bold text-white">Billing bypassed</span>
              </div>
              <p className="text-sm text-white/60">
                This account has admin / grandfathered access. All FlashTools features
                are unlocked and no usage limits apply — no subscription required.
                Remove the <code className="text-white/80">flashtools_access_enabled</code> flag
                in Django admin to return this user to normal billing.
              </p>
            </div>
          </Panel>
        )}

        {!entitlement?.comped && !entitlement?.tier && entitlement?.reserve_invited && (
          <Panel>
            <div className="flex flex-wrap items-center gap-3">
              <Badge tone="violet">Invited</Badge>
              <span className="min-w-0 flex-1 text-sm text-white">
                You've been invited to the Reserve tier. Subscribe below to unlock Buying Tools.
              </span>
            </div>
          </Panel>
        )}

        {entitlement?.tier && (
          <Panel
            title="Current plan"
            tourId="billing-current"
            actions={
              <Button variant="ghost" size="sm" onClick={handleManage} disabled={openPortal.isPending}>
                {openPortal.isPending ? 'Opening…' : 'Manage billing'}
              </Button>
            }
          >
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="text-lg font-bold text-white">
                    {entitlement.tier.charAt(0).toUpperCase() + entitlement.tier.slice(1)}
                  </h3>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <StatusPill status={entitlement.status} />
                    <TrialBadge
                      status={entitlement.status}
                      trialEnd={entitlement.trial_end}
                      currentPeriodEnd={entitlement.current_period_end}
                    />
                    {entitlement.cancel_at_period_end && entitlement.expires_at ? (
                      <span className="text-xs text-amber-400">
                        {entitlement.status === 'trialing' ? 'Trial ends' : 'Cancels'}{' '}
                        {new Date(entitlement.expires_at).toLocaleDateString()}
                      </span>
                    ) : entitlement.status === 'active' && entitlement.current_period_end ? (
                      <span className="text-xs text-white/40">
                        Renews {new Date(entitlement.current_period_end).toLocaleDateString()}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>

              {entitlement.status === 'trialing' && (
                <TrialNotice
                  trialEnd={entitlement.trial_end}
                  cancelled={entitlement.cancel_at_period_end}
                  planName={trialPlan?.name ?? 'your plan'}
                  busy={cancelTrial.isPending || resumeTrial.isPending}
                  onCancel={() => setConfirmCancelTrial(true)}
                  onResume={handleResumeTrial}
                />
              )}

              {(() => {
                const rates = entitlement.metered_rates ?? {};
                const caps = entitlement.trial_caps ?? {};
                const keys = visibleMeterKeys(entitlement.limits, entitlement.usage, rates, caps);
                if (keys.length === 0) return null;
                return (
                  <div className="grid gap-3 md:grid-cols-2">
                    {keys.map(key => (
                      <UsageMeter
                        key={key}
                        meterKey={key}
                        used={entitlement.usage[key] ?? 0}
                        included={entitlement.limits[key] ?? 0}
                        rate={rates[key]}
                        trialCap={caps[key]}
                      />
                    ))}
                  </div>
                );
              })()}
            </div>
          </Panel>
        )}

        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-white">
                {entitlement?.tier ? 'Change your plan' : 'Choose a plan'}
              </h2>
              {/* States the usage the allowances below are meant to be judged
                  against. Read off `entitlement.usage`, which the current-plan
                  meters already render — no extra request, no projection. */}
              {usedThisPeriod.length > 0 && (
                <p className="mt-1 text-xs text-white/45">
                  Used this period:{' '}
                  {usedThisPeriod.map(({ key, used }, i) => (
                    <React.Fragment key={key}>
                      {i > 0 && ' · '}
                      <span className="text-white/70 tabular-nums">{used}</span>{' '}
                      {METER_LABELS[key].toLowerCase()}
                    </React.Fragment>
                  ))}
                </p>
              )}
            </div>
            {annualAvailable && (
              <SegmentedControl
                label="Billing interval"
                size="sm"
                value={interval}
                onChange={setInterval}
                options={[
                  { value: 'month', label: 'Monthly' },
                  {
                    value: 'year',
                    label: (
                      <span className="flex items-center gap-2">
                        Annual
                        <span className="rounded-full border border-emerald-500/30 bg-emerald-500/15 px-1.5 py-0.5 text-[10px] text-emerald-300">
                          2 months free
                        </span>
                      </span>
                    ),
                  },
                ]}
              />
            )}
          </div>

          <div className="grid items-stretch gap-5 md:grid-cols-3" data-tour="billing-plans">
            {sortedPlans.map(plan => {
              const pricing = planPricing(plan, effectiveInterval);
              const isCurrent = entitlement?.tier === plan.tier;
              const isComped = Boolean(entitlement?.comped);
              const isInvitedForThisPlan = plan.invite_only && Boolean(entitlement?.reserve_invited);
              const inviteLocked = plan.invite_only && !entitlement?.reserve_invited;
              const canCheckout = !isCurrent && !entitlement?.tier && !inviteLocked && !isComped;
              const cardClass = isCurrent
                ? 'flex flex-col h-full rounded-2xl border border-[#3b82f6]/60 bg-white/5 p-6'
                : 'flex flex-col h-full rounded-2xl border border-white/10 bg-white/5 p-6';
              return (
                <div key={plan.tier} className={cardClass}>
                  <div className="flex h-full flex-col gap-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="text-lg font-bold text-white">{plan.name}</h3>
                        {/* Reserved height keeps the three cards' prices aligned */}
                        <p className="mt-1 min-h-[2.5rem] text-sm text-white/60">
                          {TIER_BLURB[plan.tier]}
                        </p>
                      </div>
                      {isCurrent && <Badge tone="blue">Current</Badge>}
                      {isInvitedForThisPlan && !isCurrent && <Badge tone="violet">Invited</Badge>}
                      {plan.invite_only && !isInvitedForThisPlan && (
                        <Badge tone="neutral">Invite only</Badge>
                      )}
                    </div>

                    <div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold text-white tabular-nums">
                          {pricing.display}
                        </span>
                        <span className="text-sm text-white/50">/{pricing.unit}</span>
                      </div>
                      {/* Reserve space so the price block height matches across cards */}
                      <div className="mt-1 min-h-[2.25rem] space-y-0.5 text-xs">
                        {pricing.subnote && (
                          <p className="text-white/50">
                            {pricing.subnote}
                            {/* `savePct` has been computed since annual shipped
                                and never shown. Same two amounts as the price
                                above it, so the two can't disagree. */}
                            {pricing.savePct > 0 && (
                              <span className="ml-1 font-medium text-emerald-300">
                                Save {pricing.savePct}%
                              </span>
                            )}
                          </p>
                        )}
                        {canCheckout && trialDays(plan) > 0 && (
                          <p className="font-medium text-emerald-300">
                            Start now — {trialDays(plan)} days free
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="border-t border-white/[0.08]" />

                    <ul className="flex-1 space-y-2.5 text-sm text-white/80">
                      {plan.features.length === 0 && (
                        <li className="flex gap-2">
                          <span className="mt-0.5 text-emerald-400">✓</span>
                          <span className="text-white/70">Base FlashTools access</span>
                        </li>
                      )}
                      {plan.features.map(key => {
                        const copy = FEATURE_COPY[key];
                        return (
                          <li key={key} className="flex gap-2">
                            <span className="mt-0.5 text-emerald-400">✓</span>
                            <span className="min-w-0">
                              <span className="text-white">{copy?.label ?? key}</span>
                              {copy?.description && (
                                <span className="block text-white/50">{copy.description}</span>
                              )}
                            </span>
                          </li>
                        );
                      })}
                      {(() => {
                        const rates = plan.metered_rates ?? {};
                        const keys = sortMeterKeys([
                          ...(Object.keys(plan.limits) as FlashtoolsMeterKey[]).filter(
                            k => (plan.limits[k] ?? 0) > 0,
                          ),
                          ...(Object.keys(rates) as FlashtoolsMeterKey[]),
                        ]);
                        return keys.map(k => {
                          const included = plan.limits[k] ?? 0;
                          const rate = rates[k];
                          // Pay-per-use meters are a cost, not a perk, so they get a
                          // "+" marker instead of a check — and the rate has to be on
                          // the card, since there's no allowance line to hang it off.
                          if (rate && included === 0) {
                            return (
                              <li key={k} className="flex gap-2">
                                <span className="mt-0.5 text-amber-300">+</span>
                                <span className="min-w-0">
                                  <span className="text-white/80">
                                    {METER_LABELS[k]}: <MeterRate>{formatUsd(rate.unit_cents)}</MeterRate> each
                                  </span>
                                  <span className="block text-white/50">
                                    Charged on top of the monthly price. No
                                    allowance — every {meterUnit(k, 1)} is billed.
                                  </span>
                                </span>
                              </li>
                            );
                          }
                          return (
                            <li key={k} className="flex gap-2">
                              <span className="mt-0.5 text-emerald-400">✓</span>
                              <span className="min-w-0">
                                <span className="text-white/80">
                                  {included} {METER_LABELS[k].toLowerCase()} included
                                </span>
                                {rate && (
                                  <span className="block text-white/50">
                                    then <MeterRateCopy meterKey={k} rate={rate} />
                                  </span>
                                )}
                              </span>
                            </li>
                          );
                        });
                      })()}
                      {/* Trial-only hard cap — disclosed up front so the limit
                          isn't a surprise mid-trial. */}
                      {trialDays(plan) > 0 &&
                        (Object.entries(plan.trial_caps ?? {}) as [FlashtoolsMeterKey, number][])
                          .filter(([, cap]) => cap > 0)
                          .map(([k, cap]) => (
                            <li key={`trial-${k}`} className="flex gap-2">
                              <span className="mt-0.5 text-white/40">•</span>
                              <span className="text-white/50">
                                Free trial limited to {cap} {METER_LABELS[k].toLowerCase()}
                              </span>
                            </li>
                          ))}
                    </ul>

                    <div className="mt-auto">
                      {isComped ? (
                        <button
                          disabled
                          className={cx(CTA_BASE, 'border border-white/10 bg-white/5 text-white/50')}
                        >
                          Unlocked (admin)
                        </button>
                      ) : isCurrent ? (
                        <button
                          onClick={handleManage}
                          disabled={openPortal.isPending}
                          className={cx(
                            CTA_BASE,
                            'border border-white/15 bg-white/5 text-white hover:bg-white/10',
                            FOCUS_RING,
                          )}
                        >
                          Manage billing
                        </button>
                      ) : inviteLocked ? (
                        <button
                          disabled
                          className={cx(CTA_BASE, 'border border-white/10 bg-white/5 text-white/50')}
                        >
                          Requires invite
                        </button>
                      ) : entitlement?.tier ? (() => {
                        const currentRank = TIER_RANK[entitlement.tier];
                        const isUpgrade = TIER_RANK[plan.tier] > currentRank;
                        const isPending = pendingTier === plan.tier;
                        return (
                          <button
                            onClick={() => requestChange(plan)}
                            disabled={changePlan.isPending}
                            className={cx(
                              CTA_BASE,
                              'border',
                              isUpgrade
                                ? 'border-[#3b82f6]/40 bg-[#3b82f6]/15 text-white hover:bg-[#3b82f6]/25'
                                : 'border-white/15 bg-white/5 text-white/80 hover:bg-white/10',
                              FOCUS_RING,
                            )}
                          >
                            {isPending
                              ? 'Switching…'
                              : `${isUpgrade ? 'Upgrade to' : 'Downgrade to'} ${plan.name}`}
                          </button>
                        );
                      })() : (
                        <button
                          onClick={() => handleSubscribe(plan)}
                          disabled={startCheckout.isPending || !canCheckout}
                          className={cx(
                            CTA_BASE,
                            'bg-[#3b82f6] text-white hover:bg-[#2563eb]',
                            FOCUS_RING,
                          )}
                        >
                          {startCheckout.isPending
                            ? 'Redirecting…'
                            : trialDays(plan) > 0
                              ? `Start ${trialDays(plan)}-day free trial`
                              : `Subscribe to ${plan.name}`}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Below the plans on purpose: the breakdown explains the number in the
            strip above, and history is reference material. Someone comparing
            tiers scrolls past the plans to reach it, which is the right order. */}
        <UpcomingInvoiceSection />
        {/* The itemisation behind those invoice lines — team members, Dice
            accounts, waitlist joins, ticket purchases. Same component the
            Profile page renders, not a copy: two lists of the same numbers
            would drift. */}
        <UsageDetailSection />
        <InvoiceHistorySection />
      </div>

      <ConfirmDialog
        open={!!confirmPlan}
        title={confirmPlan ? `Switch to ${confirmPlan.name}?` : ''}
        message={
          <>
            {confirmPlan && (
              <span className="mb-1 block text-white/80">
                {confirmPlan.name} ·{' '}
                {(() => {
                  const p = planPricing(confirmPlan, effectiveInterval);
                  return `${p.display}/${p.unit}`;
                })()}
              </span>
            )}
            {entitlement?.status === 'trialing'
              ? 'Your free trial will end immediately and your card on file will be billed for the new plan. A payment method is required.'
              : 'Stripe will prorate the change on your next invoice. Your usage limits update right away.'}
          </>
        }
        confirmLabel={confirmPlan ? `Switch to ${confirmPlan.name}` : 'Confirm'}
        busy={changePlan.isPending}
        onConfirm={confirmChange}
        onClose={() => { if (!changePlan.isPending) setConfirmPlan(null); }}
      />

      <ConfirmDialog
        open={confirmCancelTrial}
        title="Cancel your free trial?"
        message={
          <>
            You keep {trialPlan?.name ?? 'your plan'} until{' '}
            {formatDate(entitlement?.trial_end) ?? 'the end of your trial'}. After that your plan ends and
            your card won't be charged. You can change your mind any time before then.
          </>
        }
        confirmLabel="Cancel free trial"
        cancelLabel="Keep my trial"
        destructive
        busy={cancelTrial.isPending}
        onConfirm={handleCancelTrial}
        onClose={() => { if (!cancelTrial.isPending) setConfirmCancelTrial(false); }}
      />
    </PageShell>
  );
};

function formatDate(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * What happens when the trial ends, and the one action that changes it:
 * cancel (access runs to the end date, no charge) or, once cancelled, keep it.
 */
function TrialNotice({
  trialEnd,
  cancelled,
  planName,
  busy,
  onCancel,
  onResume,
}: {
  trialEnd: string | null;
  cancelled: boolean;
  planName: string;
  busy: boolean;
  onCancel: () => void;
  onResume: () => void;
}) {
  const end = formatDate(trialEnd);
  return (
    <div
      className={cx(
        'flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3',
        cancelled ? 'border-amber-500/30 bg-amber-500/10' : 'border-blue-500/25 bg-blue-500/10',
      )}
      data-testid="trial-notice"
    >
      <p className="min-w-0 flex-1 text-sm text-white/80">
        {cancelled ? (
          <>
            Trial cancelled. You have {planName} until {end ?? 'the end of your trial'}, then it ends and
            you won't be charged.
          </>
        ) : (
          <>
            Your free trial ends {end ?? 'soon'}. After that you'll be billed for {planName} unless you
            cancel.
          </>
        )}
      </p>
      {cancelled ? (
        <Button variant="secondary" size="sm" onClick={onResume} disabled={busy}>
          {busy ? 'Saving…' : 'Keep my trial'}
        </Button>
      ) : (
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
          Cancel free trial
        </Button>
      )}
    </div>
  );
}

export default BillingScreen;
